import React, { memo, useState, useEffect } from "react";
import { connect } from "react-redux";
import { ThreeDots } from "react-loader-spinner";
import { Link, useParams } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion } from "react-bootstrap";
import { faEdit, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import {
  emptyString,
  pathConstant,
  loginConstants,
  tabsNames,
  viewPathConstants,
  brandProfileConstants,
  systemGreenColor,
  screenSize,
  modalSize,
  EMPTY_ARRAY_LENGTH,
  BRAND_GROUPS_LABELS,
} from "../../constants";
import { getItemFromStorage, setItemToStorage } from "../../../../system";
import { managersConstants } from "../../../group-screens/constants";
import { managersActions } from "../../../group-screens/redux/actions";
import {
  useSpinner,
  Spinner,
  isSuperAdmin,
  isSuperOrBrandAdmin,
  handleAsyncAction,
} from "../../../../shared";
import {
  getConfirmationModal,
  addManagerPreviewModal,
} from "../../../../shared/components/popups/allModals";
import {
  advanceListPaginationQuery,
  hasMoreListItems,
  resetListPaginationFrom,
} from "../shared/listPaginationUtility";

const ManagersTab = (props) => {
  const {
    managers,
    activeTab,
    setManagers,
    queryString,
    deleteManager,
    setQueryString,
    getManagersByBrandId,
    removeManagerLocation,
    managersReducer: { allManagers },
  } = props;

  let userRole = getItemFromStorage(loginConstants.ROLE);

  const { brandId } = useParams();
  const [modalElement, setModalElement] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const managersCount = allManagers?.length ?? EMPTY_ARRAY_LENGTH;

  //Setting modal component
  const setComponent = (component) => {
    setModalElement(component);
    setShowModal(true);
  };

  //Modal to update the manager
  const updateManagerDetails = (e, manager) => {
    e.stopPropagation();
    setComponent(
      addManagerPreviewModal({
        onClose: () => setShowModal(false),
        row: manager,
        editMode: true,
        groupId: manager?.groupId,
        queryString: resetListPaginationFrom(queryString),
        brandId: brandId,
      })
    );
  };

  //Modal to delete the manager
  const deleteManagerModal = (e, managerId, managerName) => {
    e.stopPropagation();
    setComponent(
      getConfirmationModal({
        onClose: () => setShowModal(false),
        Title: BRAND_GROUPS_LABELS.CONFIRM,
        message: `Are you sure you want to delete ${managerName}?`,
        ModalSize: modalSize.MODAL_SIZE_MEDIUM,
        onConfirm: () =>
          handleAsyncAction(
            deleteManager(
              managerId,
              resetListPaginationFrom(queryString),
              brandId,
              userRole,
            ),
            { onSuccess: () => setShowModal(false) },
          ),
      })
    );
  };

  //Modal to remove the manager location
  const removeManagerLocationModal = (
    e,
    managerLocationId,
    managerId,
    managerLocationName
  ) => {
    e.stopPropagation();
    setComponent(
      getConfirmationModal({
        onClose: () => setShowModal(false),
        Title: BRAND_GROUPS_LABELS.CONFIRM,
        message: `Are you sure you want to remove ${managerLocationName}?`,
        ModalSize: modalSize.MODAL_SIZE_MEDIUM,
        onConfirm: () =>
          handleAsyncAction(
            removeManagerLocation(
              managerLocationId,
              managerId,
              resetListPaginationFrom(queryString),
              brandId,
              userRole,
            ),
            { onSuccess: () => setShowModal(false) },
          ),
      })
    );
  };

  //Setting the amount of groups already fetched
  const getManagerData = () => {
    //istanbul ignore next
    setQueryString((prevQueryString) =>
      advanceListPaginationQuery(prevQueryString, managersCount)
    );
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Handle stor name click
  const handleClickStoreName = (location) => {
    setItemToStorage(loginConstants.LOCATION_ID, location.id);
    setItemToStorage(
      brandProfileConstants.LOCATION_STATE,
      JSON.stringify({
        name: isSuperAdmin() ? pathConstant.STORE_GROUP : pathConstant.GROUP,
      })
    );
  };

  useEffect(() => {
    setManagers(allManagers);
    // eslint-disable-next-line
  }, [allManagers]);

  useEffect(() => {
    if (activeTab === tabsNames.managers) {
      //istanbul ignore next
      getManagersByBrandId(queryString, brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  return (
    <>
      {/*Modal here */}
      {showModal && modalElement}
      {/*End Modal */}
      {spinner(managersConstants.UPDATE_MANAGERS) && <Spinner />}
      <InfiniteScroll
        dataLength={managersCount}
        next={getManagerData}
        hasMore={hasMoreListItems(managersCount)}
        loader={
          spinner(managersConstants.MANAGERS_DATA) ? (
            <ThreeDots ariaLabel="loading-indicator" />
          ) : (
            emptyString
          )
        }
        scrollableTarget={
          window.innerWidth < screenSize.LAYOUT_DESKTOP
            ? emptyString
            : "main-container"
        }
      >
        <div className="vertical-scroll" aria-label="managers-tab">
          <div className="row group-header m-0">
            <div className="col-4">
              <p
                className="font-12-px bold-6 p-2 m-0"
                aria-label="manager-name-header"
              >
                {BRAND_GROUPS_LABELS.NAME}
              </p>
            </div>
            <div className="col-3">
              <p
                className="font-12-px bold-6 p-2 m-0"
                aria-label="manager-group-name-header"
              >
                {BRAND_GROUPS_LABELS.GROUP_NAME_COLUMN}
              </p>
            </div>
            <div className="col-3">
              <p
                className="font-12-px bold-6 p-2 m-0"
                aria-label="manager-store-count-header"
              >
                {BRAND_GROUPS_LABELS.STORES}
              </p>
            </div>

            <div className="col-2">
              <p
                className="font-12-px bold-6 p-2 m-0"
                aria-label="manager-actions-header"
              >
                {BRAND_GROUPS_LABELS.ACTIONS}
              </p>
            </div>
          </div>
          <Accordion>
            {managers?.length > 0 ? (
              managers?.map((manager, index) => (
                <Accordion.Item
                  key={manager?.id}
                  eventKey={index}
                  className="store-accordion pb-0"
                >
                  <Accordion.Header
                    className="accordion-header"
                    aria-label="manager-accordion-header"
                  >
                    <div className="row w-100">
                      <div className="col-4 text-break font-14-px d-flex align-items-center">
                        <p data-testid="manager-name-text" className="p-2 m-0">
                          {manager?.name}
                        </p>
                      </div>
                      <div className="col-3 text-break font-14-px d-flex align-items-center">
                        <p className="p-2 m-0">{manager?.group?.name}</p>
                      </div>
                      <div className="col-3 font-14-px d-flex align-items-center">
                        <p className="p-2 m-0">{manager?.stores?.length}</p>
                      </div>

                      <div className="col-2 d-flex align-items-center">
                        <FontAwesomeIcon
                          icon={faEdit}
                          role="button"
                          aria-hidden="true"
                          aria-label="manager-edit-icon"
                          className="fs-4 ms-1 pe-2 p-2"
                          onClick={(e) => updateManagerDetails(e, manager)}
                        />
                        <FontAwesomeIcon
                          icon={faTimesCircle}
                          aria-label="manager-delete-icon"
                          onClick={(e) =>
                            deleteManagerModal(e, manager.id, manager.name)
                          }
                          className="text-danger fs-4 ms-1 pe-2 remove-button p-2"
                        />
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body
                    className="p-2"
                    aria-label="manager-accordion-body"
                  >
                    {manager?.stores?.length > 0 ? (
                      <div className="locations-container sidebar-green font-14-px">
                        {manager?.stores.map((location) => (
                          <div
                            key={location?.id}
                            className="row item_direction"
                          >
                            <div className="col-8 pl-2 pointer font-14-px d-flex align-items-center p_55">
                              <Link
                                className="table-links"
                                aria-label="manager-stores"
                                to={`${
                                  isSuperOrBrandAdmin()
                                    ? viewPathConstants.STORES
                                    : emptyString
                                }/brand-groups/${brandId}/store-performance/${
                                  location.id
                                }`}
                                state={{
                                  name: isSuperAdmin()
                                    ? pathConstant.STORE_GROUP
                                    : pathConstant.GROUP,
                                }}
                                onClick={() => handleClickStoreName(location)}
                              >
                                {location.name}
                              </Link>
                            </div>
                            <div className="col-4 d-flex align-items-center">
                              {manager?.stores?.length > 1 && (
                                <FontAwesomeIcon
                                  icon={faTimesCircle}
                                  aria-label="manager-store-delete-icon"
                                  className="text-danger fs-4 ms-1 pe-2 remove-button p-2"
                                  onClick={(e) =>
                                    removeManagerLocationModal(
                                      e,
                                      location.id,
                                      manager?.id,
                                      location.name
                                    )
                                  }
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      BRAND_GROUPS_LABELS.NO_LOCATIONS
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))
            ) : (
              <p className="text-center d-flex justify-content-center align-items-center">
                {spinner(managersConstants.MANAGERS_DATA) ? (
                  <ThreeDots
                    ariaLabel="loading-indicator"
                    color={systemGreenColor}
                  />
                ) : (
                  <>{BRAND_GROUPS_LABELS.NO_MANAGER_FOUND}</>
                )}
              </p>
            )}
          </Accordion>
        </div>
      </InfiniteScroll>
    </>
  );
};

const mapStateToProps = (state) => ({
  managersReducer: state.managersReducer,
});

const mapDispatchToProps = (dispatch) => ({
  deleteManager: (managerId, queryString, brandId, userRole) =>
    dispatch(
      managersActions.deleteManager(managerId, queryString, brandId, userRole),
    ),
  removeManagerLocation: (storeId, managerId, queryString, brandId, userRole) =>
    dispatch(
      managersActions.removeManagerLocation(
        storeId,
        managerId,
        queryString,
        brandId,
        userRole,
      ),
    ),
  getManagersByBrandId: (queryString, brandId) =>
    //istanbul ignore next
    dispatch(managersActions.getManagersByBrandId(queryString, brandId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(ManagersTab));
